import { motion } from 'framer-motion';
import { Mail, MailCheck } from 'lucide-react';
import { useState } from 'react';
import { BottomSheet, Button } from '../components';
import { useAuth } from './AuthProvider';

/**
 * Magic-link sign-in sheet. No password — user enters their email,
 * Supabase mails them a one-tap link, the link opens the app already
 * signed in. Two visible states:
 *   - "form": email input + Send button
 *   - "sent": confirmation with the email we sent to
 *
 * Reset to the form state when the sheet closes so the next open
 * starts fresh.
 */
export function SignInSheet() {
  const { signInOpen, closeSignIn, signInWithMagicLink } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEmail('');
    setBusy(false);
    setSentTo(null);
    setError(null);
  };

  const handleClose = () => {
    closeSignIn();
    // Defer reset so the closing animation doesn't show the empty form.
    setTimeout(reset, 250);
  };

  const submit = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    const result = await signInWithMagicLink(email);
    setBusy(false);
    if (result.ok) {
      setSentTo(email.trim().toLowerCase());
    } else {
      setError(result.error);
    }
  };

  return (
    <BottomSheet
      open={signInOpen}
      onClose={handleClose}
      title={sentTo ? 'Check your inbox' : 'Sign in to Margin'}
    >
      {sentTo ? (
        <div className="px-card-pad pt-2 pb-card-pad flex flex-col items-center text-center gap-3">
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 250, damping: 22 }}
            className="w-16 h-16 rounded-pill bg-positive/15 inline-flex items-center justify-center text-positive shadow-md"
          >
            <MailCheck size={28} strokeWidth={1.5} />
          </motion.div>
          <p className="text-[14px] text-text-secondary leading-relaxed max-w-[320px]">
            We sent a magic link to{' '}
            <span className="font-mono text-text-primary">{sentTo}</span>.
            Tap the link to finish signing in. It works on any device
            you check the email on.
          </p>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleClose}
            className="mt-2"
          >
            Done
          </Button>
        </div>
      ) : (
        <div className="px-card-pad pt-2 pb-card-pad flex flex-col gap-3">
          <div className="w-12 h-12 rounded-card bg-accent/10 inline-flex items-center justify-center text-accent mx-auto mb-1">
            <Mail size={24} strokeWidth={1.5} />
          </div>
          <p className="text-[14px] text-text-secondary text-center leading-relaxed">
            Enter your email and we'll send a one-tap sign-in link. No
            password required.
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
            placeholder="you@example.com"
            autoFocus
            autoCapitalize="none"
            autoComplete="email"
            spellCheck={false}
            inputMode="email"
            className="w-full h-12 px-4 rounded-button bg-surface-secondary text-[16px] text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
          {error ? (
            <p className="text-[12px] text-negative text-center" role="alert">
              {error}
            </p>
          ) : null}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={busy || !email.trim()}
            onClick={submit}
          >
            {busy ? 'Sending…' : 'Send magic link'}
          </Button>
          <p className="text-[11px] text-text-tertiary text-center leading-relaxed">
            We only use your email to deliver the link. Your budget data
            stays on this device.
          </p>
        </div>
      )}
    </BottomSheet>
  );
}
